import { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "@unform/web";
import * as Yup from "yup";
import { KeySelectionContainer } from "../../../Pages/Private/Pix/PixTransfers/styles";
import { ArrowBack, ArrowForward, InfoOutlined } from "@mui/icons-material";
import { Button, Input } from "../../Atoms";
import { Header, KeyOptions } from "./styles";
import getValidationErrors from "../../../Utils/getValidationErrors";
import { ScreenGreeting } from "../ScreenGreeting";
import { SelectOptionButtons } from "../../Atoms/SelectOptions";
import { pixOptions } from "../../../Pages/Private/Pix/PixTransfers/pixOptions";
import { validateCPF_CNPJ } from "../../../Utils/CPF_CNPJValidation";
import {
  cleanPhoneMask,
  cleanTaxIDMask,
  handleTaxIDInput,
  phoneMask,
} from "../../../Utils";

interface SecondStepProps {
  transferAmount: number;
  handleSetStep: (step: number) => void;
  handleFetchInitialPayment: (payload: any) => void;
  loadingFetchInitialPayment: boolean;
  successFetchInitialPayment: boolean;
}

export const SecondStepPixTransfer = ({
  transferAmount,
  handleSetStep,
  handleFetchInitialPayment,
  loadingFetchInitialPayment, 
  successFetchInitialPayment,
}: SecondStepProps) => {
  const [phone, setPhone] = useState("");
  const [pixOptionSelection, setPixOptionSelection] = useState("email");
  const [taxID, setTaxID] = useState("");
  const [schema, setSchema] = useState(() => Yup.object().shape({}));
  const [inputValue, setInputValue] = useState('');
  const handleChange = (event) => { setInputValue(event.target.value);};

  const formRef = useRef(null);

  const handleSubmitPixKey = useCallback(
    async (data) => {
      try {
        await schema.validate(data, {
          abortEarly: false,
        });

        if (pixOptionSelection === "document") {
          data.document = cleanTaxIDMask(data.document);
        } else if (pixOptionSelection === "phone") {
          data.phone = `55${cleanPhoneMask(data.phone)}`;
        }

      const key = data[pixOptionSelection];

      let payload = {
        mode: "key",
        amount: transferAmount,
        documentToValidate: inputValue,
        key: {
          type: pixOptionSelection,
          value: key,
        },
      };

        handleFetchInitialPayment(payload);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [schema, pixOptionSelection, inputValue, transferAmount, handleFetchInitialPayment]
  );

  //Sets schema of the input validation yup object depending on what the user
  //chooses to insert
  useEffect(() => {
    const bollCPF_CNPJ = validateCPF_CNPJ(taxID);

    const formSchema = {
      document: Yup.object().shape({
        document: Yup.string()
          .required("CPF/CNPJ obrigatório.")
          .test("valid", "CPF/CNPJ inválido", () => bollCPF_CNPJ === true),
      }),
      email: Yup.object().shape({
        email: Yup.string()
          .email("E-mail inválido")
          .required("E-mail obrigatório"),
      }),
      randomKey: Yup.object().shape({
        randomKey: Yup.string().required("Chave obrigatória"),
      }),
      phone: Yup.object().shape({
        phone: Yup.string()
          .required("Digite o celular")
          .length(14, "Prencha o campo corretamente"),
      }),
    };

    setSchema(formSchema[pixOptionSelection]);
  }, [pixOptionSelection, taxID]);

  //Guarantees that the next step will be taken only if the
  //data sent to the API were accepted
  useEffect(() => {
    if (!!successFetchInitialPayment) handleSetStep(2);
  }, [successFetchInitialPayment]);
  return (
    <>
      <ScreenGreeting
        title={"Transferência Pix"}
        subTitle={"Informe a chave pix desejada"}
      />
      <KeySelectionContainer>
        <Header>
          <h6>Chave PIX</h6>
          <button onClick={() => handleSetStep(0)}>
            <ArrowBack />
            Voltar
          </button>
        </Header>
        <KeyOptions>
          <SelectOptionButtons
            width={150}
            defaultOption="email"
            isSelectable
            options={pixOptions}
            refreshOptionSelected={(e) => setPixOptionSelection(e)}
          />
        </KeyOptions>
        <Form
          onSubmit={handleSubmitPixKey}
          ref={formRef}
        >
          {pixOptionSelection === "email" && (
            <Input name={pixOptionSelection} type="text" placeholder="E-mail" />
          )}
          {pixOptionSelection === "document" && (
            <Input
              onChange={(e) => {
                setTaxID(handleTaxIDInput(e.currentTarget.value));
              }}
              value={taxID}
              name={pixOptionSelection}
              type="text"
              placeholder="CPF/CNPJ"
            />
          )}
          {pixOptionSelection === "phone" && (
            <Input
              onChange={(e) => {
                setPhone(phoneMask(e.currentTarget.value));
              }}
              value={phone}
              maxLength={14}
              name={pixOptionSelection}
              type="text"
              placeholder="Celular"
            />
          )}
          {pixOptionSelection === "randomKey" && (
            <Input
              name={pixOptionSelection}
              type="text"
              placeholder="Chave aleatória"
            />
          )}
          <label>
            <br />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: '#6B7280' }}>
              <InfoOutlined fontSize="small" />
              Para garantir maior segurança, por favor, informe o CPF/CNPJ do titular da chave Pix, assegurando que a conta pertence ao destinatário correto
            <br /><br />*opcional
            </span>
            <br />
          </label>
            <Input
              onChange={handleChange}
              value={inputValue}
              type="text"
              name="text"
              placeholder="CPF/CNPJ"
            />
          <Button type="submit" loading={!!loadingFetchInitialPayment}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              Continuar <ArrowForward fontSize="small" />
            </span>
          </Button>
        </Form>
      </KeySelectionContainer>
    </>
  );
};
